import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';
import { AiFillExclamationCircle } from '@react-icons/all-files/ai/AiFillExclamationCircle';
import { AiFillInfoCircle } from '@react-icons/all-files/ai/AiFillInfoCircle';
import { AiOutlineCheckCircle } from '@react-icons/all-files/ai/AiOutlineCheckCircle';
import { CgClose } from '@react-icons/all-files/cg/CgClose';
import { NotificationType } from './types';

export interface ToastProps {
  dismissTimeout?: boolean;
  children?: string | React.ReactNode;
  title: string;
  type: NotificationType;
  timeoutLength?: number;
  hideCloseButton?: boolean;
  /** Optional action label. When provided alongside `onAction`, an inline action button is rendered.
   *
   *  Canonical caller: AcordDraftBuilderView's ACORD-removal undo flow, which surfaces
   *  an "Undo" button on the success toast after removing an ACORD from a draft. */
  actionLabel?: string;
  /** Click handler for the optional action button. Called when the user clicks the action label.
   *  The handler is invoked before the toast clears. See `actionLabel` for the canonical use case. */
  onAction?: () => void;
}

interface ToastContextProps {
  toast?: ToastProps;
  setToast: (toast?: ToastProps) => void;
}
export const ToastContext = React.createContext<ToastContextProps>({
  setToast: () => {},
});

export const useToast = () => useContext(ToastContext);

const styles = {
  [NotificationType.Error]: 'bg-red-500',
  [NotificationType.Info]: 'bg-yellow-500',
  [NotificationType.Success]: 'bg-green-500',
};

const icons = {
  [NotificationType.Error]: AiFillExclamationCircle,
  [NotificationType.Info]: AiFillInfoCircle,
  [NotificationType.Success]: AiOutlineCheckCircle,
};

export function Toast() {
  const { toast: globalToast, setToast: setGlobalToast } = useToast();
  const [toast, setToast] = useState<ToastProps>();
  const Icon = toast?.type ? icons[toast.type] : undefined;
  const timeout = useRef<NodeJS.Timeout>();
  const hasAction = !!(toast?.actionLabel && toast?.onAction);

  useEffect(() => {
    if (globalToast) {
      setToast(globalToast);
    }
  }, [globalToast]);

  useEffect(() => {
    timeout.current && clearTimeout(timeout.current);
    if (toast) {
      timeout.current = setTimeout(() => {
        setGlobalToast();
      }, toast.timeoutLength || 5000);
    }
  }, [toast]);

  return (
    <div
      aria-live="assertive"
      className="fixed inset-0 z-50 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
    >
      <div className="w-full flex flex-col items-center space-y-4">
        <Transition
          show={!!globalToast}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:-translate-y-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0 sm:translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames(
              'max-w-sm w-full text-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden',
              toast?.type && styles[toast.type],
            )}
          >
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {!!Icon && <Icon className="h-6 w-6 text-white" />}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium">{toast?.title}</p>
                  <p className="mt-1 text-sm text-gray-100">
                    {toast?.children}
                  </p>
                </div>
                {hasAction && (
                  <div className="ml-3 flex-shrink-0">
                    <button
                      type="button"
                      className="text-sm font-medium text-white underline-offset-2 hover:underline focus:outline-none focus:underline"
                      onClick={() => {
                        toast!.onAction!();
                        setGlobalToast();
                      }}
                    >
                      {toast!.actionLabel}
                    </button>
                  </div>
                )}
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    type="button"
                    className="bg-transparent rounded-md inline-flex text-gray-200 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
                    onClick={() => {
                      setGlobalToast();
                    }}
                  >
                    <span className="sr-only">Close</span>
                    {!toast?.hideCloseButton && (
                      <CgClose className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
}
